import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Table, Badge } from 'react-bootstrap';
import { FaUserPlus, FaUserCheck, FaTimes, FaRedo } from 'react-icons/fa';
import { toast } from 'react-toastify';
import TestService from '../../../services/testService';

const AssignToStudents = ({ show, onHide, testId, testTitle, onSuccess }) => {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedStudents, setSelectedStudents] = useState({});
    const [maxAttempts, setMaxAttempts] = useState(1);
    const [selectAll, setSelectAll] = useState(false);

    useEffect(() => {
        if (show && testId) {
            loadStudents();
        }
    }, [show, testId]);

    useEffect(() => {
        // Update select all when individual selections change
        const totalStudents = students.length;
        const selectedCount = Object.values(selectedStudents).filter(v => v).length;
        setSelectAll(totalStudents > 0 && selectedCount === totalStudents);
    }, [selectedStudents, students]);

    const loadStudents = async () => {
        try {
            setLoading(true);
            const response = await TestService.getTestStudents(testId);
            const studentsData = response.data || [];
            setStudents(studentsData);
            
            // Preselect already assigned students
            const preselected = {};
            studentsData.forEach(student => {
                if (student.is_assigned) {
                    preselected[student.id] = true;
                }
            });
            setSelectedStudents(preselected);
            
            // Set max attempts from first assigned student if any
            const assignedStudent = studentsData.find(s => s.is_assigned);
            if (assignedStudent) {
                setMaxAttempts(assignedStudent.max_attempts || 1);
            }
        } catch (error) {
            console.error('Failed to load students:', error);
            toast.error('Failed to load students');
        } finally {
            setLoading(false);
        }
    };

    const toggleSelectAll = () => {
        if (selectAll) {
            setSelectedStudents({});
        } else {
            const allSelected = {};
            students.forEach(student => {
                allSelected[student.id] = true;
            });
            setSelectedStudents(allSelected);
        }
    };

    const toggleStudent = (studentId) => {
        setSelectedStudents(prev => ({
            ...prev,
            [studentId]: !prev[studentId]
        }));
    };

    const handleAssign = async () => {
        const selectedIds = Object.keys(selectedStudents).filter(key => selectedStudents[key]);
        
        if (selectedIds.length === 0) {
            toast.warning('Please select at least one student');
            return;
        }

        try {
            setSaving(true);
            const response = await TestService.assignTestToStudents(testId, selectedIds, maxAttempts);
            toast.success(response.message || `Test assigned to ${response.data?.assigned || selectedIds.length} students`);
            onSuccess();
            onHide();
        } catch (error) {
            console.error('Failed to assign:', error);
            toast.error(error.response?.data?.message || 'Failed to assign test to students');
        } finally {
            setSaving(false);
        }
    };

    const getStatusBadge = (student) => {
        if (student.attempt_count >= student.max_attempts && student.max_attempts > 0) {
            return <Badge bg="danger">Max Attempts Reached</Badge>;
        } else if (student.attempt_count > 0) {
            return <Badge bg="warning">Attempts: {student.attempt_count}/{student.max_attempts}</Badge>;
        } else if (student.is_assigned) {
            return <Badge bg="success">Assigned</Badge>;
        }
        return <Badge bg="secondary">Not Assigned</Badge>;
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FaUserPlus className="me-2 text-primary" />
                    Assign Test to Students
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="info">
                    <strong>Test:</strong> {testTitle}
                    <br />
                    <small>Select students to assign this test. Assigned students will be able to attempt the test.</small>
                </Alert>

                <Form.Group className="mb-3">
                    <Form.Label>Maximum Attempts per Student</Form.Label>
                    <Form.Control
                        type="number"
                        min="1"
                        max="10"
                        value={maxAttempts}
                        onChange={(e) => setMaxAttempts(parseInt(e.target.value) || 1)}
                    />
                    <Form.Text className="text-muted">
                        Number of times a student can attempt this test (default: 1)
                    </Form.Text>
                </Form.Group>

                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2">Loading students...</p>
                    </div>
                ) : students.length === 0 ? (
                    <Alert variant="warning" className="text-center">
                        No students found for this test's course/batch.
                    </Alert>
                ) : (
                    <>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <strong>{students.length} Students Available</strong>
                            <Button variant="outline-secondary" size="sm" onClick={toggleSelectAll}>
                                {selectAll ? 'Deselect All' : 'Select All'}
                            </Button>
                        </div>
                        
                        <div className="border rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <Table striped hover size="sm" className="mb-0">
                                <thead className="sticky-top bg-white">
                                    <tr>
                                        <th style={{ width: '50px' }}>Select</th>
                                        <th>Student Name</th>
                                        <th>Email</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {students.map(student => (
                                        <tr key={student.id}>
                                            <td className="text-center">
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={selectedStudents[student.id] || false}
                                                    onChange={() => toggleStudent(student.id)}
                                                />
                                            </td>
                                            <td>
                                                <strong>{student.name}</strong>
                                            </td>
                                            <td>{student.email}</td>
                                            <td>{getStatusBadge(student)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                        
                        <div className="mt-2 text-muted small">
                            Selected: {Object.values(selectedStudents).filter(v => v).length} students
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={saving}>
                    Cancel
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleAssign} 
                    disabled={saving || Object.values(selectedStudents).filter(v => v).length === 0}
                >
                    {saving ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Assigning...
                        </>
                    ) : (
                        <>
                            <FaUserCheck className="me-2" />
                            Assign to Selected ({Object.values(selectedStudents).filter(v => v).length})
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AssignToStudents;